import React, { ErrorInfo } from 'react';
import { BaseError, ErrorOptions, createErrorInstanceOf, createErrorMarker, createError } from '@adv-frontend/error';
import type { JSONValue } from '@direct-frontend/stdlib';

/** Параметры ошибки */
interface ReactRenderErrorOptions {
    /** Название компонента, которое будет доставлено в сообщение об ошибке */
    componentName?: string;
    /** Стек компонентов из errorInfo в componentDidCatch */
    componentStack?: string | null;
}

// Стек компонентов может быть очень глубоким (более 100 фреймов).
// Такие большие стеки не может прожевать Error Booster, а для отладки обычно достаточно первых
// 2-3 фреймов.
const COMPONENT_STACK_LIMIT = 15;

/**
 * Ошибка предназначена для логирования ошибок рендера внутри componentDidCatch.
 * Добавляет componentStack в цепочку cause, чтобы со стеком можно было удобно работать в Error Booster.
 */
export class ReactRenderError extends BaseError<JSONValue, Record<string, string> | undefined> {
    /**
     * @param renderErrorOptions Информация про ошибку рендера
     * @param options Стандартные опции BaseError (cause и additional)
     */
    constructor(
        renderErrorOptions: ReactRenderErrorOptions,
        options: ErrorOptions<JSONValue, Record<string, string> | undefined>
    ) {
        const { componentName, componentStack } = renderErrorOptions;
        const component = componentName ? ` "${componentName}"` : '';
        const message = `Unexpected error while rendering${component} component`;

        const cause = parseComponentsError(componentStack, options.cause);

        super(message, {
            ...options,
            cause,
        });

        Object.assign(this, { [createErrorMarker('ReactRenderError')]: true });
    }

    /** Имя конструктора ошибки */
    public readonly name = 'ReactRenderError';
}

Object.defineProperty(ReactRenderError, Symbol.hasInstance, {
    value: createErrorInstanceOf('ReactRenderError'),
    configurable: true,
});

const ReactComponentStack = createError('ReactComponentStack');

function parseComponentsError(componentStack: string | null | undefined, cause: unknown) {
    if (!componentStack) {
        return cause;
    }

    try {
        const err = new ReactComponentStack('captured', { cause });
        const stack = componentStack.split('\n');
        err.stack = ['ReactRenderError', ...stack.slice(1, COMPONENT_STACK_LIMIT)].join('\n');
        return err;
    } catch (e) {
        console.warn(e);
        return cause;
    }
}
/** Пропсы компонента `ErrorBoundary` */
export interface ErrorBoundaryProps {
    /** Компонент, который оборачиавется в ErrorBoundary для обработки его ошибок */
    children: React.ReactNode;
    /** Функция, отображающая ошибку в ErrorBoundary */
    errorFallback: (params: { error: unknown; onReset?: () => void }) => React.ReactElement | null;
    /** Метод для логирования ошибок */
    logError?: (error: unknown, errorInfo: ErrorInfo) => void;
    /** Название компонента для логирования ошибки */
    componentName?: string;
}

/** Стейт компонента ErrorBoundary */
interface ErrorBoundaryState {
    /** Признак наличия ошибки при рендере компонентов */
    hasError: boolean;
    /** Ошибка, полученная из компонента, который обёрнут в ErrorBoundary */
    error: unknown;
}

/**
 * Компонент ErrorBoundary, перехватывающий ошибки вложенного компонента и отображающий их в интерфейсе
 * @kind component
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = {
        hasError: false,
        error: undefined,
    };

    override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
        const { logError, componentName } = this.props;

        if (logError) {
            logError(error, errorInfo);
        } else {
            console.error(
                new ReactRenderError(
                    {
                        componentName,
                        componentStack: errorInfo.componentStack,
                    },
                    { cause: error }
                )
            );
        }
    }

    private reset = () => {
        this.setState({ hasError: false, error: undefined });
    };

    /**
     * @unusedCodePublic используется внутри React
     */
    static getDerivedStateFromError(error: unknown) {
        return { hasError: true, error };
    }

    override render() {
        const { children, errorFallback } = this.props;
        const { error, hasError } = this.state;

        if (!hasError) {
            return children;
        }

        // Вызываем как функцию (не JSX-компонент), чтобы смена идентичности не перемонтировала поддерево.
        return errorFallback({ error, onReset: this.reset });
    }
}
